import { CommonModule } from '@angular/common';
import {
    Component,
    Input,
    InputSignal,
    OnDestroy,
    Signal,
    WritableSignal,
    computed,
    inject,
    input,
    signal,
} from '@angular/core';
import { DatiqTemplateModule } from '@dat/datiq-components-pkg/template';
import {
    EventInfo,
    IqEventBusBrokerHandler,
    IqEventBusBrokerService,
    IqEventBusNode,
    IqEventBusTransformationService,
    matchQuery,
} from '@dat/iq-event-bus';
import {
    IqCssClassProcessorDirective,
    IqStyleProcessorCssClassComponent,
    IqStyleProcessorShowElementComponent,
} from '@dat/iq-style-processor';
import { IqTooltipTooltipComponent } from '@dat/iq-tooltip';
import { camelCase, get, isEmpty, omit } from 'lodash';
import { IqBuilderComponentComponent } from '../iq-builder-component/iq-builder-component.component';
import { RouteInfo, Section, TriggerEvent } from '../models';
import { IqBuilderStateService } from '../state';
import { IqBuilderAuditToolsService } from '../iq-builder-audit-tools/iq-builder-audit-tools.service';

type RuleResponseTypes = 'ACTION';
type SectionEvaluatorSuccessPayload = EventInfo & {
    data: {
        [key: string]: any;
        dashboardId: string;
        sectionId: string;
        type: RuleResponseTypes;
    };
};

@Component({
    selector: 'iq-builder-section',
    standalone: true,
    templateUrl: './iq-builder-section.component.html',
    styleUrl: './iq-builder-section.component.scss',
    imports: [
        CommonModule,
        IqBuilderComponentComponent,
        DatiqTemplateModule,
        IqStyleProcessorShowElementComponent,
        IqStyleProcessorCssClassComponent,
        IqCssClassProcessorDirective,
        IqTooltipTooltipComponent,
    ],
})
export class IqBuilderSectionComponent implements OnDestroy {
    section: InputSignal<Section> = input(null);

    ruleStatus: WritableSignal<Record<string, boolean>> = signal({});

    @Input()
    id: string;

    @Input()
    dashboardId: string;

    dashboardCache = computed(() => {
        return this._state.dashboardCache(this.dashboardId)();
    });

    setRunRules(id: string, value: boolean) {
        this.ruleStatus.set({
            ...this.ruleStatus(),
            [id]: value,
        });
    }

    templateData: Signal<any> = computed(() => {
        const dashboardCache = this.dashboardCache();

        return {
            dashboardCache,
            cache: this.section().cache,
            section: this.section(),
            dashboardId: this.dashboardId,
            route: this._routeInfo(),
        };
    });

    collapsed = signal(true);

    dbId: Signal<string> = computed(() => {
        return `dashboard:${this.dashboardId}::section:${this.id}`;
    });

    private readonly _routeInfo: WritableSignal<RouteInfo> = signal<RouteInfo>({
        analyticsPageName: '',
        data: {},
        locked: false,
        pageTitle: '',
        params: {},
        path: '',
        loaded: false,
    });

    private _auditToolService = inject(IqBuilderAuditToolsService);

    private readonly _eventBroker: IqEventBusBrokerService = inject(
        IqEventBusBrokerService,
    );

    private readonly _transform: IqEventBusTransformationService = inject(
        IqEventBusTransformationService,
    );

    private _deregister: () => void;
    private _registeredAction: Record<string, IqEventBusBrokerHandler>;

    constructor(private readonly _state: IqBuilderStateService) {
        this._state.registerSectionActions();

        this._registeredAction = this._eventBroker.registerMap({
            'type:rules:evaluator:run:success': {
                callback: (eventInfo: SectionEvaluatorSuccessPayload) => {
                    this._runRules(eventInfo);
                },
            },
            'type:route:changed': {
                callback: (routeInfo: RouteInfo) => {
                    this._routeInfo.set({
                        ...routeInfo,
                        loaded: true,
                    });
                },
            },
        });

        this._deregister = this._eventBroker.watchHandledEvents(
            (node: IqEventBusNode) => {
                if (!node.evt?.name?.startsWith('type:')) return;

                const eventName = camelCase(
                    node.evt.name.replace('type', 'on'),
                );

                const actionArray = this.section()?.[eventName] ?? [];
                if (!actionArray.length) return;

                const templateData = this.getTemplateData(node.evt.payload);
                this._auditToolService.addPrerenderedAction({
                    id: node.id,
                    template: JSON.stringify(actionArray, null, 4),
                    data: templateData,
                    eventName,
                });
                const filteredActions: TriggerEvent[] =
                    actionArray
                        ?.filter?.((action: TriggerEvent) =>
                            !isEmpty(action.sourceEventRule)
                                ? matchQuery(
                                      node.evt,
                                      this._transform.renderJson(
                                          JSON.stringify(
                                              action.sourceEventRule ?? {},
                                              null,
                                              4,
                                          ),
                                          {
                                              ...templateData,
                                              dashboardId: this.dashboardId,
                                              sectionId: this.id,
                                          },
                                          {},
                                          (
                                              err: any,
                                              template: string,
                                              data: any,
                                          ) => {
                                              this._logFilterActionsErrors(
                                                  eventName,
                                                  node,
                                                  err,
                                                  template,
                                                  data,
                                              );
                                          },
                                      ) ?? {},
                                      action.sourceEventRuleOperator ?? 'AND',
                                  )
                                : true,
                        )
                        ?.map((x) => omit(x, ['sourceEventRule'])) ?? [];

                if (isEmpty(filteredActions)) return;

                const renderedActions = this._transform
                    .renderJson(
                        JSON.stringify(filteredActions, null, 4),
                        {
                            ...templateData,
                            dashboardId: this.dashboardId,
                            sectionId: this.id,
                        },
                        {},
                        (err: any, template: string, data: any) => {
                            this._logRenderedErrors(
                                eventName,
                                node,
                                err,
                                template,
                                data,
                            );
                        },
                    )
                    .map(
                        (evt: EventInfo): EventInfo => ({
                            ...evt,
                            parentId: node.id,
                            meta: {
                                sectionId: this.id,
                                dashboardId: this.dashboardId,
                            },
                        }),
                    );

                if (!renderedActions?.length) return;

                this._state.processActions(renderedActions, {
                    ...templateData,
                    dashboardId: this.dashboardId,
                    sectionId: this.id,
                    type: 'ACTION' as RuleResponseTypes,
                });
            },
        );
    }

    private _logFilterActionsErrors(
        eventName: string,
        node: IqEventBusNode,
        err: any,
        template: string,
        data: any,
    ) {
        this._auditToolService.addFilterActionsError({
            id: node.id,
            template,
            data,
            eventName,
            error: err,
        });

        throw err;
    }
    private _logRenderedErrors(
        eventName: string,
        node: IqEventBusNode,
        err: any,
        template: string,
        data: any,
    ) {
        this._auditToolService.addRenderedActionError({
            id: node.id,
            template,
            data,
            eventName,
            error: err,
        });

        throw err;
    }

    private _runRules(eventInfo: SectionEvaluatorSuccessPayload) {
        if (
            get(eventInfo, 'data.dashboardId') !== this.dashboardId ||
            get(eventInfo, 'data.sectionId') !== this.id
        )
            return;

        const response: Record<RuleResponseTypes, () => any> = {
            ACTION: () => {
                this._eventBroker.dispatch(eventInfo);
            },
        };

        if (!response[eventInfo?.data?.type]) return;

        response[eventInfo.data.type]?.();
    }

    showTooltip: Signal<boolean> = computed(() => {
        return !!this.section()?.tooltipBody?.length;
    });

    ngOnDestroy(): void {
        this._deregister();
        this._eventBroker.unregisterMap(this._registeredAction);
    }

    private getTemplateData($event: any) {
        return {
            ...$event,
            ...this.templateData(),
            route: this._routeInfo(),
        };
    }
}
